import { useQueryClient } from '@tanstack/react-query';

import { useFishGroupsController_relocate } from '@/services/hooks';
import type { RelocateFishGroupRequest as RelocateFishGroupRequestDto } from '@/services/types';

import type { MutationState } from '../shared/mutation';

import { invalidateFishGroupQueries } from './_invalidateQueries';
import type { RelocateFishGroupRequest } from './operationTypes';

export interface UseRelocateFishGroupResult extends MutationState {
    relocate: (requestBody: RelocateFishGroupRequest) => Promise<void>;
}

interface UseRelocateFishGroupOptions {
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
}

export const useRelocateFishGroup = (
    clientId: number,
    fishGroupId: number,
    options?: UseRelocateFishGroupOptions
): UseRelocateFishGroupResult => {
    const queryClient = useQueryClient();
    const mutation = useFishGroupsController_relocate();

    const relocate = async (requestBody: RelocateFishGroupRequest): Promise<void> => {
        try {
            // The domain shape is wire-compatible with the generated DTO;
            // the cast keeps the boundary explicit so we never leak the
            // generated type back into UI callers (per `services/` rule).
            await mutation.mutateAsync({
                clientId,
                fishGroupId,
                requestBody: requestBody as RelocateFishGroupRequestDto,
            });
            await invalidateFishGroupQueries(queryClient, clientId, fishGroupId);
            options?.onSuccess?.();
        } catch (error) {
            options?.onError?.(error);
            throw error;
        }
    };

    return {
        relocate,
        isLoading: mutation.isPending,
        error: mutation.error,
        reset: mutation.reset,
    };
};
